import { LocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RedirectAuthLoginResponse } from '@talisdev/persona-padlock';
import { NEVER, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as URI from 'urijs';

import { AuthProvider } from '®shared/auth/auth-provider';
import { AuthServiceLogin } from '®shared/auth/auth-service-login';
import { AuthorizationFailedException } from '®shared/auth/authorization-failed-exception';
import { PersonaPkceSharedService } from '®shared/auth/persona-pkce-shared.service';
import { User } from '®shared/auth/user';
import { UserScopesService } from '®shared/auth/user-scopes.service';
import { AppConfig } from '®shared/config/app-config';

import {
  PersonaPadlockService,
  RedirectAuthAppState,
} from './persona-padlock.service';

const REDIRECT_PATH = 'redirect-authentication.html';

@Injectable()
export class PersonaPkceAuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  constructor(
    private personaPadlockService: PersonaPadlockService,
    private personaPkceSharedService: PersonaPkceSharedService,
    private locationStrategy: LocationStrategy,
    appConfig: AppConfig,
    router: Router,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, router, userScopesService);
  }

  public obtainNewToken(returnUrl?: string): Observable<User> {
    const currentUri = returnUrl ? new URI(returnUrl) : this.getCurrentUri();
    const tenantCode = this.appConfig.tenant.shortCode;
    const baseHref = this.locationStrategy.getBaseHref();

    let redirectUri = `${currentUri.origin()}${baseHref}3/${tenantCode}/${REDIRECT_PATH}`;

    const langParam = currentUri.query(true).lang;
    if (langParam) {
      redirectUri = new URI(redirectUri).addQuery('lang', langParam).toString();
    }

    const fullPath = currentUri.resource();
    const returnPathAndQuery = fullPath.startsWith(baseHref)
      ? `/${fullPath.slice(baseHref.length)}`
      : fullPath;

    const appState = {
      target: returnPathAndQuery,
      tenantCode,
    };

    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    try {
      this.personaPadlockService.initiateRedirectAuthentication(
        config,
        redirectUri,
        appState,
      );

      return NEVER;
    } catch (error) {
      return this.personaPkceSharedService.throwAuthenticationFailedException(
        error,
      );
    }
  }

  public getCurrentToken(): Observable<User> {
    const currentUri = this.getCurrentUri();
    const authProvider = this.personaPkceSharedService.getAuthProvider(
      this.isTalisLoginUri(currentUri),
    );
    const bypassFirstPartyCheck = true;
    const config = this.personaPkceSharedService.getPersonaConfig(authProvider);

    return this.personaPkceSharedService.silentlyGetCurrentToken(
      this.personaPadlockService.silentlyAuthenticate(
        config,
        bypassFirstPartyCheck,
      ),
    );
  }

  public refreshToken(): Observable<User> {
    return this.personaPkceSharedService.refreshToken(this.currentUser);
  }

  public login(): void {
    const returnUrl = this.getCurrentUri();
    returnUrl.removeQuery('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public loginDuringRouteNavigation(stateUrl: string): void {
    const urlBase = this.getCurrentUri().origin();
    const returnUrl = new URL(stateUrl, urlBase);
    returnUrl.searchParams.delete('talis_login');

    this.obtainNewToken(returnUrl.toString());
  }

  public completeRedirectAuthentication(): Observable<
    RedirectAuthLoginResponse<RedirectAuthAppState>
  > {
    return this.personaPadlockService.completeRedirectAuthentication().pipe(
      tap(({ appState }) => {
        if (this.appConfig.tenant.shortCode !== appState.tenantCode) {
          throw new AuthorizationFailedException('Invalid tenant');
        }
      }),
    );
  }
}
